import { ShieldCheckIcon } from "@heroicons/react/outline";
import { useEffect, useRef, useState } from "react";

import { BreakGlassPasswordSection } from "@/routes/settings/AccountSettings/BreakGlassPasswordSection";
import { MfaEnrollmentDialog } from "@/routes/settings/AccountSettings/MfaEnrollmentDialog";
import {
  getInitialBreakGlassSetupStep,
  needsBreakGlassPassword,
  needsBreakGlassSetup,
  type BreakGlassSetupStep,
} from "@/routes/settings/AccountSettings/breakGlassPolicy";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth-context";
import { useMfaPolicy } from "@/queries/identity/useMfaPolicy";

const STEP_COPY: Record<BreakGlassSetupStep, { title: string; body: string }> = {
  intro: {
    title: "Set up emergency access",
    body: "As an organization Owner, you need a PromptLayer MFA factor and emergency password before your team can enable Require SSO. This lets you sign in if your identity provider is unavailable.",
  },
  mfa: {
    title: "Enroll multi-factor authentication",
    body: "Complete MFA enrollment in the dialog, then continue to set your emergency password.",
  },
  password: {
    title: "Set emergency access password",
    body: "Choose a password for email sign-in when SSO is required but your identity provider is down.",
  },
};

export const BreakGlassSetupDialog = () => {
  const auth = useAuth();
  const isLoggedIn = Boolean(auth?.userToken && auth.user);
  const { data: mfaPolicy, isLoading, refetch } = useMfaPolicy();
  const shouldPrompt = isLoggedIn && !isLoading && needsBreakGlassSetup(mfaPolicy);

  const [step, setStep] = useState<BreakGlassSetupStep>("intro");
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const wasPromptingRef = useRef(false);

  useEffect(() => {
    if (shouldPrompt && !wasPromptingRef.current) {
      setStep(getInitialBreakGlassSetupStep(mfaPolicy));
    }
    wasPromptingRef.current = shouldPrompt;
  }, [shouldPrompt, mfaPolicy]);

  if (!shouldPrompt) {
    return null;
  }

  const handleMfaComplete = async () => {
    setMfaDialogOpen(false);
    const { data: updatedPolicy } = await refetch();
    if (needsBreakGlassPassword(updatedPolicy)) {
      setStep("password");
    }
  };

  const handlePasswordComplete = () => {
    void refetch();
  };

  const handleIntroContinue = () => {
    if (mfaPolicy?.is_enrolled === false) {
      setStep("mfa");
      setMfaDialogOpen(true);
      return;
    }
    setStep("password");
  };

  const copy = STEP_COPY[step];

  return (
    <>
      <Dialog open onOpenChange={() => undefined}>
        <DialogContent
          className="sm:max-w-lg"
          shouldShowCloseButton={false}
          onPointerDownOutside={event => event.preventDefault()}
          onEscapeKeyDown={event => event.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
              <DialogTitle>{copy.title}</DialogTitle>
            </div>
            <DialogDescription className="pt-2">{copy.body}</DialogDescription>
          </DialogHeader>

          {step === "intro" && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
              <li>Enroll PromptLayer MFA for emergency sign-in</li>
              <li>Set an emergency access password</li>
              <li>Required before enabling Require SSO for your organization</li>
            </ul>
          )}

          {step === "password" && (
            <BreakGlassPasswordSection embedded onSuccess={handlePasswordComplete} />
          )}

          {step === "intro" && (
            <DialogFooter>
              <Button onClick={handleIntroContinue}>Get started</Button>
            </DialogFooter>
          )}

          {step === "mfa" && !mfaDialogOpen && (
            <DialogFooter>
              <Button onClick={() => setMfaDialogOpen(true)}>Continue MFA setup</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <MfaEnrollmentDialog
        open={mfaDialogOpen}
        onOpenChange={setMfaDialogOpen}
        onEnrollmentComplete={() => {
          void handleMfaComplete();
        }}
      />
    </>
  );
};
